import * as THREE from 'three'
import type { GLTFLoader, GLTFParser } from 'three/addons/loaders/GLTFLoader.js'
import { SkinnedMeshNotSupportedError } from '@/plugins/mesh/errors'
import { createMeshLoadResult } from '@/plugins/mesh/loaders/createMeshLoadResult'
import { MeshPrimitiveNormalizer } from '@/plugins/mesh/MeshPrimitiveNormalizer'
import { MeshResourceTracker } from '@/plugins/mesh/MeshResourceTracker'
import type { MeshLoadResult } from '@/plugins/mesh/types'

interface NodeDefinition {
  mesh?: number
  skin?: number
  name?: string
  matrix?: number[]
  translation?: number[]
  rotation?: number[]
  scale?: number[]
  children?: number[]
  weights?: number[]
  extensions?: Record<string, unknown>
}

type ShallowParser = GLTFParser & {
  _loadNodeShallow?: (index: number) => Promise<THREE.Object3D>
}

function localMatrix(node: NodeDefinition): THREE.Matrix4 {
  if (node.matrix) return new THREE.Matrix4().fromArray(node.matrix)
  return new THREE.Matrix4().compose(
    new THREE.Vector3().fromArray(node.translation ?? [0, 0, 0]),
    new THREE.Quaternion().fromArray(node.rotation ?? [0, 0, 0, 1]),
    new THREE.Vector3().fromArray(node.scale ?? [1, 1, 1])
  )
}

/** 隔离 Three.js 0.180 节点浅加载接入点，并管理部分解析及迟到资源。 */
export class ProgressiveGltfSession {
  private closed = false
  private readonly disposedResources = new WeakSet<object>()
  private readonly delivered = new Set<number>()
  private readonly normalizer = new MeshPrimitiveNormalizer()

  constructor(
    private readonly resources: MeshResourceTracker,
    private readonly onPart: (part: MeshLoadResult) => void,
    private readonly format: 'glb' | 'gltf',
    private readonly source: string,
    private readonly signal?: AbortSignal
  ) {}

  /** 注册观察器，不改变解析器最终场景的层级、节点与资源。 */
  install(loader: { register?: GLTFLoader['register'] }, allowPrimitiveParts = true): void {
    loader.register?.((parser) => ({
      name: 'SHARE3D_progressive_mesh',
      beforeRoot: () => {
        const target = parser as ShallowParser
        const shallow = target._loadNodeShallow
        if (!shallow) return null
        const nodes = (parser.json.nodes ?? []) as NodeDefinition[]
        const parents = new Map<number, THREE.Matrix4>()
        const visit = (index: number, parent: THREE.Matrix4) => {
          if (parents.has(index) || !nodes[index]) return
          const node = nodes[index]
          if (node.skin !== undefined)
            throw new SkinnedMeshNotSupportedError(this.source, node.name)
          parents.set(index, parent)
          const world = parent.clone().multiply(localMatrix(node))
          for (const child of node.children ?? []) visit(child, world)
        }
        for (const index of parser.json.scenes?.[parser.json.scene ?? 0]?.nodes ?? []) {
          visit(index, new THREE.Matrix4())
        }
        const pending = new Map<number, Promise<THREE.Object3D>>()
        const primitiveMeshes = new Set<number>()
        const loadMesh = target.loadMesh
        target.loadMesh = (meshIndex) => {
          const definition = parser.json.meshes[meshIndex]
          const instances = [...parents.keys()].filter((index) => nodes[index].mesh === meshIndex)
          // strip/fan 会在 loadMesh 内克隆并转换几何，不能同时构建预览与完整 Mesh。
          const supportsParts =
            allowPrimitiveParts &&
            definition.primitives.length > 1 &&
            !definition.isSkinnedMesh &&
            definition.primitives.every(
              (primitive: { targets?: unknown; mode?: number }) =>
                !primitive.targets && (primitive.mode === undefined || primitive.mode === 4)
            ) &&
            instances.length > 0 &&
            instances.every((index) => !nodes[index].extensions && !nodes[index].weights)
          if (!supportsParts) return loadMesh.call(target, meshIndex)
          primitiveMeshes.add(meshIndex)
          const parts = definition.primitives.map((primitive: unknown, primitiveIndex: number) => {
            // 独立 JSON/命名视图，共享 Three.js 的几何、材质、纹理 Promise 缓存。
            const view = Object.create(target) as ShallowParser & { nodeNamesUsed: object }
            const meshes = [...parser.json.meshes]
            meshes[meshIndex] = { ...definition, primitives: [primitive] }
            view.json = { ...parser.json, meshes }
            view.nodeNamesUsed = {}
            view.getDependency = (type, index) =>
              target.getDependency(type, index).then((value) => {
                if (type === 'material') this.acceptResource(value)
                return value
              })
            view.loadGeometries = (primitives) =>
              target.loadGeometries.call(view, primitives).then((geometries) => {
                for (const geometry of geometries) this.acceptResource(geometry)
                return geometries
              })
            return loadMesh.call(view, meshIndex).then((object) => {
              if (this.closed || this.signal?.aborted) {
                this.disposeLate(object)
                return
              }
              this.resources.trackObject3D(object)
              for (const index of instances) {
                const wrapper = new THREE.Group()
                wrapper.matrixAutoUpdate = false
                wrapper.matrix.copy(parents.get(index)!).multiply(localMatrix(nodes[index]))
                const copy = object.clone(true)
                copy.name = `${nodes[index].name ?? `node_${index}`}:primitive_${primitiveIndex}`
                wrapper.add(copy)
                wrapper.updateMatrixWorld(true)
                this.deliver(wrapper)
              }
            })
          })
          // 原解析流程保留最终层级、命名、扩展及 associations。
          const complete = loadMesh.call(target, meshIndex).then((object) => {
            if (this.closed || this.signal?.aborted) this.disposeLate(object)
            else this.resources.trackObject3D(object)
            return object
          })
          return Promise.all([complete, ...parts]).then(([object]) => object)
        }
        target._loadNodeShallow = (index) => {
          let promise = pending.get(index)
          if (!promise) {
            promise = shallow.call(target, index).then((node) => {
              if (this.closed || this.signal?.aborted) {
                this.disposeLate(node)
                return node
              }
              this.resources.trackObject3D(node)
              const parent = parents.get(index)
              if (
                parent &&
                nodes[index]?.mesh !== undefined &&
                !primitiveMeshes.has(nodes[index].mesh!) &&
                !this.delivered.has(index)
              ) {
                this.delivered.add(index)
                const wrapper = new THREE.Group()
                wrapper.matrixAutoUpdate = false
                wrapper.matrix.copy(parent)
                wrapper.add(node.clone(true))
                wrapper.updateMatrixWorld(true)
                this.deliver(wrapper)
              }
              return node
            })
            pending.set(index, promise)
          }
          return promise
        }
        return null
      },
    }))
  }

  private deliver(wrapper: THREE.Object3D): void {
    if (this.closed || this.signal?.aborted) return
    const normalized = this.normalizer.normalize(wrapper, this.resources)
    if (normalized.primitives.length > 0) {
      this.onPart(
        createMeshLoadResult(this.format, normalized, { ignoredAnimations: [], warnings: [] }, 0)
      )
    }
  }

  private acceptResource(resource: THREE.BufferGeometry | THREE.Material): void {
    if (!this.closed && !this.signal?.aborted) {
      this.resources.trackOther(resource)
    } else {
      const late = new MeshResourceTracker()
      late.trackOther(resource)
      this.disposeLateResources(late)
    }
  }

  /** 停止交付并记录即将释放的资源身份，避免迟到场景重复释放共享资源。 */
  close(): void {
    this.closed = true
    for (const set of [
      this.resources.geometries,
      this.resources.materials,
      this.resources.textures,
      this.resources.others,
    ]) {
      for (const resource of set) this.disposedResources.add(resource)
    }
  }

  /** 清理取消/失败后才就绪的源节点，按共享资源身份去重。 */
  disposeLate(root: THREE.Object3D): void {
    const late = new MeshResourceTracker()
    late.trackObject3D(root)
    this.disposeLateResources(late)
  }

  private disposeLateResources(late: MeshResourceTracker): void {
    this.close()
    for (const set of [late.geometries, late.materials, late.textures, late.others]) {
      for (const resource of set) {
        if (this.disposedResources.has(resource)) continue
        this.disposedResources.add(resource)
        try {
          resource.dispose()
        } catch {
          /* 继续清理其余迟到资源。 */
        }
      }
    }
  }
}
